/**
 * MatchLeadPicker — lightweight modal for attaching an orphan RC activity row
 * to a lead. Used by Communications CRM Activity rows whose `lead_id` is null.
 *
 * Match order (top → bottom):
 *   1. Exact phone match (normalized digits)
 *   2. Partial phone match (last 4+ digits)
 *   3. Name/company substring match on typed query
 *   4. All other leads (alphabetical)
 *
 * Keeps scope minimal: single `/leads` fetch, client-side ranking. No new
 * backend search endpoint is introduced.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, Search, Check, Phone } from "lucide-react";
import { API, useAuth } from "@/App";

function normalizePhone(s) {
  return (s || "").replace(/[^0-9]/g, "");
}

function rankLeads(leads, activityPhone, query) {
  const aDigits = normalizePhone(activityPhone);
  const aTail = aDigits.slice(-4);
  const q = (query || "").trim().toLowerCase();
  const scored = leads.map((l) => {
    const lDigits = normalizePhone(l.phone);
    let score = 0;
    if (aDigits && lDigits && lDigits === aDigits) score += 1000;
    else if (aTail && lDigits && lDigits.endsWith(aTail)) score += 500;
    if (q) {
      const hay = `${l.contact_name || ""} ${l.company || ""} ${l.title || ""}`.toLowerCase();
      if (hay.includes(q)) score += 100;
    }
    return { l, score };
  });
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const aName = (a.l.contact_name || a.l.title || "").toLowerCase();
    const bName = (b.l.contact_name || b.l.title || "").toLowerCase();
    return aName.localeCompare(bName);
  });
  return scored.map((s) => s.l);
}

export default function MatchLeadPicker({ activityPhone, onCancel, onPick }) {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setLeads(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (!cancelled) setLeads([]);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const ranked = useMemo(() => rankLeads(leads, activityPhone, query), [leads, activityPhone, query]);

  return (
    <div
      data-testid="match-lead-picker"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
      style={{
        position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
        background: "rgba(0,0,0,0.35)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div style={{ width: 480, maxHeight: "80vh", background: "#fff", borderRadius: 10, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "12px 16px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <Phone style={{ width: 14, height: 14, color: "var(--blue)" }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 650 }}>Match activity to a lead</div>
            {activityPhone && (
              <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                Phone: <strong style={{ fontFamily: "monospace" }}>{activityPhone}</strong>
              </div>
            )}
          </div>
          <button type="button" data-testid="match-lead-cancel" onClick={onCancel}
                  style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)" }}>
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>
        <div style={{ padding: "10px 16px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <Search style={{ width: 12, height: 12, color: "var(--text-tertiary)" }} />
          <input
            data-testid="match-lead-search"
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, company, or title"
            style={{ border: "none", flex: 1, fontSize: 12, padding: "4px 6px" }}
          />
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div data-testid="match-lead-loading" style={{ padding: 32, textAlign: "center", fontSize: 11, color: "var(--text-tertiary)" }}>Loading leads…</div>
          ) : ranked.length === 0 ? (
            <div data-testid="match-lead-empty" style={{ padding: 32, textAlign: "center", fontSize: 11, color: "var(--text-tertiary)" }}>
              No leads found. Try a different search.
            </div>
          ) : ranked.slice(0, 50).map((l) => {
            const phoneMatch = normalizePhone(l.phone) && normalizePhone(l.phone) === normalizePhone(activityPhone);
            return (
              <button
                key={l.id}
                data-testid={`match-lead-row-${l.id}`}
                disabled={busyId === l.id}
                onClick={async () => { setBusyId(l.id); try { await onPick(l); } finally { setBusyId(null); } }}
                style={{
                  width: "100%", textAlign: "left", padding: "10px 16px",
                  borderBottom: "0.5px solid var(--border)",
                  background: busyId === l.id ? "#f3f3f3" : "#fff",
                  cursor: busyId === l.id ? "wait" : "pointer", border: "none",
                  display: "flex", alignItems: "center", gap: 10,
                }}
                onMouseOver={(e) => { if (busyId !== l.id) e.currentTarget.style.background = "#fafafa"; }}
                onMouseOut={(e) => { if (busyId !== l.id) e.currentTarget.style.background = "#fff"; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>
                    {l.contact_name || l.title || "Unnamed lead"}
                    {phoneMatch && (
                      <span data-testid={`match-lead-exact-${l.id}`}
                            style={{ marginLeft: 6, fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 8, background: "var(--green-light)", color: "var(--green)" }}>
                        <Check style={{ width: 8, height: 8, display: "inline", marginRight: 2 }} />exact phone
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                    {l.company || "—"}{l.phone ? ` · ${l.phone}` : ""}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
